// Session du compte connecté : chargée au démarrage (`/api/auth/me`), vidée sur un 401.
// Mot de passe provisoire : l'application entière se réduit à l'écran de changement.
// Chaque changement de session porte une génération : une réponse arrivée après une connexion
// ou une déconnexion plus récente est ignorée.
import type { AccountDto } from '@crenow/shared';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { api, ApiError, onAuthProblem } from './api.ts';
import { ephemeral, resetEphemeral } from './ephemeral.ts';

interface SessionValue {
  /** `undefined` : chargement ; `null` : déconnecté. */
  account: AccountDto | null | undefined;
  login: (login: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [account, setAccountState] = useState<AccountDto | null | undefined>(undefined);
  const generation = useRef(0);
  const currentId = useRef<number | null>(null);

  /** Nouveau compte (ou aucun) : l'état client de l'ancien ne passe pas au suivant. */
  const setAccount = useCallback((next: AccountDto | null) => {
    if (next?.id !== currentId.current) resetEphemeral();
    currentId.current = next?.id ?? null;
    if (next && !next.mustChangePassword) ephemeral.provisional = null;
    setAccountState(next);
  }, []);

  const refresh = useCallback(async () => {
    const at = ++generation.current;
    try {
      const { account: me } = await api<{ account: AccountDto }>('GET', '/auth/me');
      if (at === generation.current) setAccount(me);
    } catch (error) {
      if (at !== generation.current) return;
      if (error instanceof ApiError && error.status === 401) setAccount(null);
      else throw error;
    }
  }, [setAccount]);

  useEffect(() => {
    refresh().catch(() => setAccount(null));
    onAuthProblem((status) => {
      if (status === 401) {
        generation.current++;
        setAccount(null);
      } else setAccountState((current) => (current ? { ...current, mustChangePassword: true } : current));
    });
    return () => onAuthProblem(null);
  }, [refresh, setAccount]);

  const value = useMemo<SessionValue>(
    () => ({
      account,
      refresh,
      login: async (login, password) => {
        const at = ++generation.current;
        const provisional = ephemeral.provisional;
        const { account: me } = await api<{ account: AccountDto }>('POST', '/auth/login', { login, password });
        if (at !== generation.current) return;
        resetEphemeral();
        currentId.current = me.id;
        // Gardé seulement pour le changement forcé qui suit.
        ephemeral.provisional = me.mustChangePassword ? provisional : null;
        setAccountState(me);
      },
      logout: async () => {
        generation.current++;
        await api('POST', '/auth/logout').catch(() => undefined);
        setAccount(null);
      },
    }),
    [account, refresh, setAccount],
  );
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error('SessionProvider manquant');
  return value;
}

/** Compte connecté (dans les routes protégées). */
export function useAccount(): AccountDto {
  const { account } = useSession();
  if (!account) throw new Error('Compte requis');
  return account;
}
